import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import * as TaxReportsActions from "./tax-reports.actions";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {TaxReportModel, TaxReportSearchParams, TaxReportTypes} from '@accounting/_common/models/tax-report.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const taxReportFeatureKey = 'taxReport';

const typeOrder: Record<TaxReportTypes, number> = {
    'TAX_CALCULATION': 0,
    'BALANCE': 1,
    'FIN': 2,
    'MONTH_TAX_CALCULATION': 3
};

export function sortTaxReport(b: TaxReportModel, a: TaxReportModel) {
    if (a.year !== b.year) {
        return a.year - b.year;
    }
    if (a.taxReportType !== b.taxReportType) {
        return typeOrder[a.taxReportType] - typeOrder[b.taxReportType];
    }
    if (a.quarter !== b.quarter) {
        return (a.quarter || 0) - (b.quarter || 0);
    }
    if (a.month !== b.month) {
        return (a.month || 0) - (b.month || 0);
    }
    if (a.created !== b.created) {
        return (a.created || '').localeCompare(b.created || '');
    }
    return 0;
}

export const adapter: EntityAdapter<TaxReportModel> = createEntityAdapter<TaxReportModel>({
    sortComparer: sortTaxReport
});

export type TaxReportState = EntityStateModel<TaxReportModel, TaxReportSearchParams>;

export const initialState: TaxReportState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const taxReportReducer = createReducer(
    initialState,

    on(TaxReportsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(TaxReportsActions.LoadListSuccess, (state, action) => {
        return adapter.setAll(action.payload.items, {
            ...state,
            pagination: action.payload.pagination,
            listLoading: undefined
        });
    }),

    on(TaxReportsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(TaxReportsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(TaxReportsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(TaxReportsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(TaxReportsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(TaxReportsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(TaxReportsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(TaxReportsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(TaxReportsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: TaxReportSearchParams | undefined, item: TaxReportModel): boolean => {

    if (!searchParams) {
        return true;
    }

    if (searchParams.taxReportType && searchParams.taxReportType != item.taxReportType) {
        return false;
    }

    if (searchParams.year && searchParams.year != item.year) {
        return false;
    }

    if (searchParams.quarter && searchParams.quarter != item.quarter) {
        return false;
    }

    return true;
};
